import { useFormContext } from 'react-hook-form';
import { type FC, memo, useCallback, useEffect, useMemo } from 'react';
import { FormContainer } from 'components/layout';
import { Box, Typography } from '@mui/material';
import type { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import type { GridRow } from '../api/getAdSets';
import { ChipInput, GroupedSelectableGrid } from 'components/common';
import { useAdSetsData } from '../hooks';
import datadogActions from 'lib/datadog';
import { useLocationParams } from 'hooks';
import { toIdSelectionEntries } from '../utils/idSelection';

const EMPTY_SELECTION: string[] = [];

const DataGridAdSet = () => {
  const { watch, setValue, trigger, formState: { errors } } = useFormContext();
  const selectedAdGroups: string[] = watch('selectedAdGroups') || EMPTY_SELECTION;
  const adGroupErrorMessage = errors?.selectedAdGroups?.message;
  const { setSelectedAdSets: setSelectedAdSetsParam } = useLocationParams();

  const { gridRows, isLoading, isAdGroupRow, isError, error } = useAdSetsData();

  const COLUMNS: GridColDef<GridRow>[] = useMemo(() => [
    {
      field: 'campaign_name',
      headerName: 'Name',
      width: 400,
      flex: 1,
      renderCell: (params: GridRenderCellParams<GridRow>) => {
        if (isAdGroupRow(params.row)) {
          return (
            <Box sx={{ display: 'flex', height: '100%', alignItems: 'center' }}>
              <Typography
                variant="body2"
                sx={{ display: 'flex', alignItems: 'center' }}
              >
                {params.row.adgroup_name}
              </Typography>
            </Box>
          );
        }

        return (
          <Box sx={{ display: 'flex', height: '100%', alignItems: 'center' }}>
            <Typography
              variant="body2"
              sx={{ display: 'flex', alignItems: 'center' }}
            >
              {params.row.campaign_name}
            </Typography>
          </Box>
        );
      },
    },
  ], [isAdGroupRow]);

  useEffect(() => {
    void trigger('selectedAdGroups');
  }, [trigger]);

  useEffect(() => {
    if (!isError || !error) {
      return;
    }

    datadogActions.trackDatadogError(error as Error, {
      source: 'tiktok_choose_adset_query',
    });
  }, [isError, error]);

  const handleSelectionMode = useCallback(
    (rows: string[]) => {
      setValue('selectedAdGroups', rows, { shouldValidate: true });
    },
    [setValue],
  );

  const getRowId = useCallback(
    (row: GridRow) => (isAdGroupRow(row) ? row.adgroup_id : row.campaign_id),
    [isAdGroupRow],
  );

  useEffect(() => {
    setSelectedAdSetsParam(toIdSelectionEntries(selectedAdGroups.map(String)));
  }, [selectedAdGroups, setSelectedAdSetsParam]);

  return (
    <GroupedSelectableGrid
      rows={gridRows}
      columns={COLUMNS}
      isChildRow={isAdGroupRow}
      getRowId={getRowId}
      selectedIds={selectedAdGroups.map(String)}
      onSelectionChange={handleSelectionMode}
      isLoading={isLoading}
      footerMessage={adGroupErrorMessage as string | undefined}
    />
  );
};

const ChooseAdSet: FC = memo(() => {
  const { watch, formState: { errors } } = useFormContext();
  const isCampaignFieldError = errors?.selectedCampaigns?.message as string;
  const filterAdSetsByName = watch('filterAdSetsByName') as string[] | undefined;
  const { setFilterAdSetsByName: setFilterAdSetsByNameParam } = useLocationParams();

  useEffect(() => {
    setFilterAdSetsByNameParam(filterAdSetsByName ?? []);
  }, [filterAdSetsByName, setFilterAdSetsByNameParam]);

  return (
    <FormContainer
      title={'Choose Campaigns'}
      error={isCampaignFieldError}
      sx={{ flexDirection: 'column', alignItems: 'flex-start', gap: 2, height: '100%' }}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-start', width: '100%' }}>
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            flex: 1,
            flexDirection: 'row',
            alignItems: 'start',
            flexWrap: 'nowrap',
          }}
        >
          <ChipInput
            name="filterAdSetsByName"
            inputLabel="Enter adset name"
          />
        </Box>
      </Box>
      <DataGridAdSet />
    </FormContainer>
  );
});
ChooseAdSet.displayName = 'ChooseAdSet';

export default ChooseAdSet;